import { ChangeDetectionStrategy, Component, Input, computed, inject } from '@angular/core';

import { UsageService } from '../shared/services/usage.service';

/**
 * Avatar with an ambient usage ring (prototype "Avatar ring gauge"). The ring
 * fills with the caller's monthly token usage and shifts colour by level —
 * brand red (ok) → amber (approaching) → danger (reached) — so usage is
 * glanceable from the always-visible sidebar avatar without an extra row. The
 * actual numbers live on demand in the account menu (the usage-meter).
 *
 * Pure presentation bound to UsageService signals; the service owns fetching.
 */
@Component({
  selector: 'app-usage-ring',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span
      class="usage-ring"
      [class]="'usage-ring--' + level()"
      [style.width.px]="size"
      [style.height.px]="size"
      [attr.title]="ringTitle()"
    >
      <svg class="usage-ring__svg" [attr.width]="size" [attr.height]="size"
        [attr.viewBox]="'0 0 ' + size + ' ' + size" aria-hidden="true">
        <circle class="usage-ring__track" [attr.cx]="centre" [attr.cy]="centre"
          [attr.r]="radius" fill="none" [attr.stroke-width]="stroke"></circle>
        @if (showArc()) {
          <circle class="usage-ring__progress" [attr.cx]="centre" [attr.cy]="centre"
            [attr.r]="radius" fill="none" [attr.stroke-width]="stroke"
            stroke-linecap="round"
            [attr.stroke-dasharray]="circumference"
            [attr.stroke-dashoffset]="dashOffset()"
            [attr.transform]="'rotate(-90 ' + centre + ' ' + centre + ')'"></circle>
        }
      </svg>
      <span class="usage-ring__avatar" [style.font-size.px]="fontSize">{{ initials }}</span>
    </span>
  `,
  styles: [`
    .usage-ring { position: relative; display: inline-flex; flex-shrink: 0; line-height: 0; }
    .usage-ring__svg { position: absolute; inset: 0; }
    .usage-ring__track { stroke: var(--wg-surface-3, #ededf0); }
    /* Prototype palette: brand red (ok) -> amber (approaching) -> red (reached). */
    .usage-ring__progress {
      stroke: #d81f2c;
      transition: stroke-dashoffset 0.45s ease, stroke 0.2s ease;
    }
    .usage-ring--approaching .usage-ring__progress { stroke: #e0a03a; }
    .usage-ring--reached .usage-ring__progress { stroke: #e5484d; }
    .usage-ring__avatar {
      position: absolute;
      inset: 4px;
      border-radius: 50%;
      background: #d81f2c;
      color: var(--wg-text-on-accent, #fff);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      letter-spacing: -0.02em;
      line-height: 1;
    }
  `],
})
export class UsageRingComponent {
  private readonly svc = inject(UsageService);

  /** Outer diameter of the ring in px. The avatar sits 4px inside it. */
  @Input() size = 34;
  /** Initials rendered in the avatar centre. */
  @Input() initials = '';

  /** Ring stroke width — fixed; the avatar inset (4px) is sized to clear it. */
  readonly stroke = 2.5;
  get centre(): number { return this.size / 2; }
  get radius(): number { return (this.size - this.stroke * 2) / 2; }
  get circumference(): number { return 2 * Math.PI * this.radius; }
  get fontSize(): number { return Math.round(this.size * 0.34); }

  readonly level = this.svc.monthlyLevel;
  readonly pct = this.svc.monthlyPct;

  /** Only draw the arc when we have a finite, non-zero monthly usage to show. */
  readonly showArc = computed(() =>
    this.svc.usage() != null && !this.svc.unlimited() && this.pct() > 0);

  readonly dashOffset = computed(() =>
    this.circumference * (1 - Math.min(this.pct(), 100) / 100));

  readonly ringTitle = computed(() => {
    if (this.svc.usage() == null) return 'Token usage';
    if (this.svc.unlimited()) return 'Token usage: unlimited';
    return `${this.pct()}% of monthly tokens used`;
  });
}
